export interface RouteToolHistoryRecord {
  id: string;
  entityName: string;
  uploadDate: string;
  uploadTime: string | null;
  clinicSites: number | null;
  warehouses: number | null;
  remoteUsers: number | null;
  status: string;
}

export interface RouteToolEntityOption {
  value: string;
  label: string;
}

const ROUTE_TOOL_HISTORY_STORAGE_KEY = 'routeToolHistoryRecords';
const ROUTE_TOOL_HISTORY_MAX_RECORDS = 200;

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getPathValue = (source: any, path: string): unknown => {
  const segments = path.split('.');
  let current = source;

  for (const segment of segments) {
    if (!current || typeof current !== 'object') {
      return undefined;
    }
    current = current[segment];
  }

  return current;
};

const pickFirstNumeric = (source: any, paths: string[]): number | null => {
  for (const path of paths) {
    const candidate = toNullableNumber(getPathValue(source, path));
    if (candidate !== null) {
      return candidate;
    }
  }

  return null;
};

const formatDate = (value: unknown): string => {
  const parsed = value ? new Date(String(value)) : new Date();
  if (Number.isNaN(parsed.getTime())) {
    const now = new Date();
    return now.toISOString().slice(0, 10);
  }

  return parsed.toISOString().slice(0, 10);
};

const formatTime = (value: unknown): string | null => {
  if (!value) {
    return null;
  }

  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const sortByDateDesc = (records: RouteToolHistoryRecord[]) =>
  [...records].sort((a, b) => b.uploadDate.localeCompare(a.uploadDate));

export const getRouteToolHistoryRecords = (): RouteToolHistoryRecord[] => {
  try {
    const raw = window.localStorage.getItem(ROUTE_TOOL_HISTORY_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    const records = parsed
      .map((item: any): RouteToolHistoryRecord | null => {
        const entityName = String(item?.entityName || '').trim();
        if (!entityName) {
          return null;
        }

        return {
          id: String(item?.id || `${entityName}-${Date.now()}`),
          entityName,
          uploadDate: formatDate(item?.uploadDate),
          uploadTime: item?.uploadTime ? String(item.uploadTime) : null,
          clinicSites: toNullableNumber(item?.clinicSites),
          warehouses: toNullableNumber(item?.warehouses),
          remoteUsers: toNullableNumber(item?.remoteUsers),
          status: String(item?.status || 'Completed'),
        };
      })
      .filter((item: RouteToolHistoryRecord | null): item is RouteToolHistoryRecord => Boolean(item));

    return sortByDateDesc(records);
  } catch {
    return [];
  }
};

export const saveRouteToolHistoryRecords = (records: RouteToolHistoryRecord[]) => {
  const sorted = sortByDateDesc(records).slice(0, ROUTE_TOOL_HISTORY_MAX_RECORDS);
  window.localStorage.setItem(ROUTE_TOOL_HISTORY_STORAGE_KEY, JSON.stringify(sorted));
};

export const updateRouteToolEntityCounts = (
  entityName: string,
  counts: {
    clinicSites?: number | null;
    warehouses?: number | null;
    remoteUsers?: number | null;
  }
) => {
  const existing = getRouteToolHistoryRecords();
  const updated = existing.map((record) => {
    if (record.entityName !== entityName) {
      return record;
    }

    return {
      ...record,
      clinicSites:
        counts.clinicSites !== undefined ? counts.clinicSites : record.clinicSites,
      warehouses:
        counts.warehouses !== undefined ? counts.warehouses : record.warehouses,
      remoteUsers:
        counts.remoteUsers !== undefined ? counts.remoteUsers : record.remoteUsers,
    };
  });

  saveRouteToolHistoryRecords(updated);
};

export const appendRouteToolHistoryRecord = (record: RouteToolHistoryRecord) => {
  const existing = getRouteToolHistoryRecords();
  saveRouteToolHistoryRecords([record, ...existing]);
};

export const getRouteToolEntityOptions = (): RouteToolEntityOption[] => {
  const records = getRouteToolHistoryRecords();
  const uniqueEntities = Array.from(
    new Set(records.map((record) => record.entityName).filter((name) => Boolean(name.trim())))
  );

  return uniqueEntities.map((name) => ({ value: name, label: name }));
};

export const createHistoryRecordFromUpload = (
  entityName: string,
  resultPayload: any
): RouteToolHistoryRecord => {
  const recordId =
    resultPayload?.id ||
    resultPayload?.upload_id ||
    resultPayload?.record_id ||
    `${entityName}-${Date.now()}`;

  const timestampCandidate =
    resultPayload?.uploadDate ||
    resultPayload?.upload_date ||
    resultPayload?.created_at ||
    resultPayload?.timestamp ||
    resultPayload?.uploaded_at ||
    resultPayload?.data?.uploadDate ||
    resultPayload?.data?.upload_date ||
    resultPayload?.data?.created_at ||
    resultPayload?.data?.timestamp ||
    new Date().toISOString();

  return {
    id: String(recordId),
    entityName,
    uploadDate: formatDate(timestampCandidate),
    uploadTime: formatTime(timestampCandidate),
    clinicSites: pickFirstNumeric(resultPayload, [
      'clinicSites',
      'clinic_sites',
      'site_count',
      'sites',
      'counts.clinicSites',
      'counts.clinic_sites',
      'counts.sites',
      'summary.clinicSites',
      'summary.clinic_sites',
      'summary.sites',
      'data.clinicSites',
      'data.clinic_sites',
      'data.site_count',
      'data.sites',
      'data.counts.clinicSites',
      'data.counts.clinic_sites',
      'data.summary.clinicSites',
      'data.summary.clinic_sites',
    ]),
    warehouses: pickFirstNumeric(resultPayload, [
      'warehouses',
      'warehouse_count',
      'counts.warehouses',
      'counts.warehouse_count',
      'summary.warehouses',
      'summary.warehouse_count',
      'data.warehouses',
      'data.warehouse_count',
      'data.counts.warehouses',
      'data.counts.warehouse_count',
      'data.summary.warehouses',
      'data.summary.warehouse_count',
    ]),
    remoteUsers: pickFirstNumeric(resultPayload, [
      'remoteUsers',
      'remote_users',
      'user_count',
      'users',
      'counts.remoteUsers',
      'counts.remote_users',
      'counts.user_count',
      'counts.users',
      'summary.remoteUsers',
      'summary.remote_users',
      'summary.user_count',
      'summary.users',
      'data.remoteUsers',
      'data.remote_users',
      'data.user_count',
      'data.users',
      'data.counts.remoteUsers',
      'data.counts.remote_users',
      'data.summary.remoteUsers',
      'data.summary.remote_users',
    ]),
    status: String(resultPayload?.status || resultPayload?.state || 'Completed'),
  };
};
